import mutationRegisterPatient from './mutation-register-patient.graphql.js'
import mutationAddEmployeeProfessionalRelationship from './mutation-add-employee-professional-relationship.graphql.js'
import mutationAddPatientToLead from './mutation-add-patient-to-lead.graphql.js'
import queryPersonMatches from './query-person-matches.graphql.js'
import { useSetFlowTo, normalizePath } from 'Simple/Flow.js'
import {
  notifyError,
  useNotifications,
  notifySuccess,
} from 'Logic/Notifications.js'
import { useMutation, useClient } from 'Data/Api'
import { useDataValue, useDataSubmit } from 'Simple/Data.js'
import { patientName } from 'Data/format.js'

function prepareReferralsToInsert(refs) {
  let template_referrals = refs
    .filter(ref => ref.text_value)
    .map(ref => ({
      patient_id: ref.patient_id,
      referral_template_id: ref.referral_template_id,
      text_value: ref.text_value,
    }))

  let person_referrals = refs
    .filter(ref => ref.person_type)
    .map(ref => ({
      patient_id: ref.patient_id,
      person_id: ref.person_id,
      person_type: ref.person_type,
    }))
  return { person_referrals, template_referrals }
}

/** @type {import('Simple/types.js').useDataOnSubmit} */
export default function useDataOnSubmit(props, data) {
  let onActionRegisterPatient = useDataOnActionRegisterPatient(props, data)
  let onActionGoToPatient = useDataOnActionGoToPatient(props, data)
  let onActionAddPatientToPerson = useDataOnActionAddPatientToPerson(
    props,
    data
  )

  return async function onSubmit({ value, args }) {
    switch (args?.type) {
      case 'registerPatient': {
        return onActionRegisterPatient({ value, args })
      }
      case 'addPatientToPerson': {
        return onActionAddPatientToPerson({ value, args })
      }
      case 'goToPatient': {
        return onActionGoToPatient({ value, args })
      }
      default: {
        return
      }
    }
  }
}

/** @type {import('Simple/types.js').useDataOnSubmit} */
function useDataOnActionAddPatientToPerson(props) {
  let [, notify] = useNotifications()
  let setFlowTo = useSetFlowTo(props.viewPath)
  let [, executeMutationRegisterPatient] = useMutation(mutationRegisterPatient)
  let [, executeMutationNewEmployeeProfessionalRelationship] = useMutation(
    mutationAddEmployeeProfessionalRelationship
  )

  let current_location_id = useDataValue({
    viewPath: props.viewPath,
    context: 'global',
    path: 'current_location.id',
  })
  let parent_company_id = useDataValue({
    viewPath: props.viewPath,
    context: 'global',
    path: 'current_location.parent_company.id',
  })
  let global_submit = useDataSubmit({
    context: 'global',
    viewPath: props.viewPath,
  })

  return async function onAction({ value, args }) {
    let { person_referrals, template_referrals } = prepareReferralsToInsert(
      value.referrals
    )

    let patient_data = {
      patient: {
        organization_id: parent_company_id,
        primary_location_id: current_location_id,
        person_id: args.person_id,
        patient_locations: {
          data: {
            location_id: current_location_id,
          },
        },
        patient_person_referrals: {
          data: person_referrals,
        },
        patient_template_referrals: {
          data: template_referrals,
        },
        ...(value.provider_id
          ? {
              professional_relationships: {
                data: {
                  dtype: 'emploProfessionalRelationship',
                  past: false,
                },
              },
            }
          : {}),
      },
    }

    let mutationRegisterPatientResponse =
      await executeMutationRegisterPatient(patient_data)

    if (mutationRegisterPatientResponse?.error) {
      notify(
        notifyError(
          'Something went wrong. Please, try again or contact support if the problem persists.'
        )
      )
      return
    }
    let patient =
      mutationRegisterPatientResponse.data?.insert_vaxiom_patients_one
    let professional_relationship = patient.professional_relationships?.[0]

    // This has to be a separate mutations because of this bug
    // https://github.com/hasura/graphql-engine/issues/8232

    if (value.provider_id) {
      await executeMutationNewEmployeeProfessionalRelationship({
        input: {
          provider_id: value.provider_id,
          id: professional_relationship.id,
        },
      })
    }

    notify(notifySuccess('Patient registered'))

    global_submit({
      type: 'tabs/add',
      tab: {
        id: patient._id,
        type: 'Patient',
        name: patientName(patient.person),
        tab: 'profile',
        viewPath: '/App/Account/Content/Patients',
        patient_id: patient._id,
      },
    })

    setFlowTo(normalizePath(props.viewPath, '../No'))
    return
  }
}

/** @type {import('Simple/types.js').useDataOnSubmit} */
function useDataOnActionRegisterPatient(props) {
  let client = useClient()
  let setFlowTo = useSetFlowTo(props.viewPath)
  let [, notify] = useNotifications()
  let [, executeMutationRegisterPatient] = useMutation(mutationRegisterPatient)
  let [, executeMutationAddPatientToLead] = useMutation(
    mutationAddPatientToLead
  )
  let [, executeMutationNewEmployeeProfessionalRelationship] = useMutation(
    mutationAddEmployeeProfessionalRelationship
  )

  let current_location_id = useDataValue({
    viewPath: props.viewPath,
    context: 'global',
    path: 'current_location.id',
  })
  let parent_company_id = useDataValue({
    viewPath: props.viewPath,
    context: 'global',
    path: 'current_location.parent_company.id',
  })
  let global_submit = useDataSubmit({
    context: 'global',
    viewPath: props.viewPath,
  })

  return async function onAction({ value, args }) {
    if (args.check_existing) {
      let response = await client
        .query(queryPersonMatches, {
          first_name: `%${value.person.first_name.trim()}%`,
          last_name: `%${value.person.last_name.trim()}%`,
          birth_date: value.person.birth_date,
          birth_date_s: value.person.birth_date,
        })
        .toPromise()

      if (
        response.data?.vaxiom_persons_aggregate.aggregate.count ||
        response.data?.leads_leads_aggregate.aggregate.count
      ) {
        setFlowTo(normalizePath(props.viewPath, './Matches/Content'))
        return
      }
    }

    let { person_referrals, template_referrals } = prepareReferralsToInsert(
      value.referrals
    )
    let patient_data = {
      patient: {
        organization_id: parent_company_id,
        primary_location_id: current_location_id,
        person: {
          data: {
            ...value.person,
            ssn: value.person.ssn?.trim() === '' ? null : value.person.ssn,
            organization_id: parent_company_id,
          },
        },
        patient_locations: {
          data: {
            location_id: current_location_id,
          },
        },
        patient_person_referrals: {
          data: person_referrals,
        },
        patient_template_referrals: {
          data: template_referrals,
        },
        ...(value.provider_id
          ? {
              professional_relationships: {
                data: {
                  dtype: 'emploProfessionalRelationship',
                  past: false,
                },
              },
            }
          : {}),
      },
    }

    let mutationRegisterPatientResponse =
      await executeMutationRegisterPatient(patient_data)
    if (mutationRegisterPatientResponse?.error) {
      return notify(
        notifyError(
          'Something went wrong. Please, try again or contact support if the problem persists.'
        )
      )
    }

    let patient =
      mutationRegisterPatientResponse.data?.insert_vaxiom_patients_one
    let professional_relationship = patient.professional_relationships?.[0]

    // This has to be a separate mutations because of this bug
    // https://github.com/hasura/graphql-engine/issues/8232

    if (value.provider_id) {
      await executeMutationNewEmployeeProfessionalRelationship({
        input: {
          provider_id: value.provider_id,
          id: professional_relationship.id,
        },
      })
    }

    if (args.lead_id) {
      await executeMutationAddPatientToLead({
        lead_id: args.lead_id,
        patient_id: patient._id,
      })
    }

    notify(notifySuccess('Patient registered'))

    global_submit({
      type: 'tabs/add',
      tab: {
        id: patient._id,
        type: 'Patient',
        name: patientName(patient.person),
        tab: args.add_patient_to_lead ? 'treatment_card' : 'profile',
        viewPath: '/App/Account/Content/Patients',
        patient_id: patient._id,
      },
    })

    setFlowTo(normalizePath(props.viewPath, '../No'))
  }
}

/** @type {import('Simple/types.js').useDataOnSubmit} */
function useDataOnActionGoToPatient(props) {
  let setFlowTo = useSetFlowTo(props.viewPath)
  let global_submit = useDataSubmit({
    context: 'global',
    viewPath: props.viewPath,
  })

  return async function onAction({ args }) {
    global_submit({
      type: 'tabs/add',
      tab: {
        id: args.id,
        type: 'Patient',
        name: patientName(args.person),
        tab: 'profile',
        viewPath: '/App/Account/Content/Patients',
        patient_id: args.id,
      },
    })
    setFlowTo(normalizePath(props.viewPath, '../No'))
    return
  }
}
